import React from 'react';
import { Calendar, Clock, AlertTriangle, FileText, Receipt, Building } from 'lucide-react';

const TaxCalendar = () => {
  // Tax deadlines - Updated for FY 2025-26
  const taxDeadlines = [
    {
      category: 'GST',
      icon: <Receipt size={20} />,
      color: '#FF8C42',
      deadlines: [
        { date: '11th of every month', description: 'GSTR-1 Filing (Monthly)', type: 'monthly' },
        { date: '13th of every month', description: 'GSTR-1 IFF (Quarterly)', type: 'quarterly' },
        { date: '20th of every month', description: 'GSTR-3B Filing', type: 'monthly' },
        { date: '31st December', description: 'GST Annual Return (GSTR-9)', type: 'annual' },
      ]
    },
    {
      category: 'Income Tax',
      icon: <FileText size={20} />,
      color: '#1B5A7E',
      deadlines: [
        { date: '15th June', description: 'Advance Tax - 1st Installment (15%)', type: 'quarterly' },
        { date: '15th September', description: 'Advance Tax - 2nd Installment (45%)', type: 'quarterly' },
        { date: '15th December', description: 'Advance Tax - 3rd Installment (75%)', type: 'quarterly' },
        { date: '15th March', description: 'Advance Tax - 4th Installment (100%)', type: 'quarterly' },
        { date: '31st July', description: 'ITR Filing (Non-Audit Cases)', type: 'annual' },
        { date: '31st October', description: 'ITR Filing (Audit Cases)', type: 'annual' },
      ]
    },
    {
      category: 'TDS',
      icon: <Building size={20} />,
      color: '#4CAF50',
      deadlines: [
        { date: '7th of every month', description: 'TDS Payment (Non-Govt)', type: 'monthly' },
        { date: '31st of quarter end month', description: 'TDS Return Filing', type: 'quarterly' },
        { date: '31st May', description: 'TDS Certificate (Form 16/16A)', type: 'annual' },
      ]
    }
  ];

  // Get upcoming deadlines (next 30 days simulation)
  const getUpcomingDeadlines = () => {
    const today = new Date();
    const currentMonth = today.toLocaleString('default', { month: 'long' });
    const currentDay = today.getDate();
    
    const upcoming = [];
    
    // Add monthly deadlines
    if (currentDay <= 7) {
      upcoming.push({ date: '7th ' + currentMonth, desc: 'TDS Payment Due', urgent: currentDay >= 5, category: 'TDS' });
    }
    if (currentDay <= 11) {
      upcoming.push({ date: '11th ' + currentMonth, desc: 'GSTR-1 Filing Due', urgent: currentDay >= 9, category: 'GST' });
    }
    if (currentDay <= 20) {
      upcoming.push({ date: '20th ' + currentMonth, desc: 'GSTR-3B Filing Due', urgent: currentDay >= 18, category: 'GST' });
    }
    
    return upcoming.slice(0, 3);
  };

  const upcomingDeadlines = getUpcomingDeadlines();

  return (
    <section className="tax-calendar-section" data-testid="tax-calendar-section">
      <div className="container">
        <div className="tax-calendar-header">
          <div className="tax-calendar-icon">
            <Calendar size={32} />
          </div>
          <div>
            <h2 className="section-title-center">Important Tax Deadlines</h2>
            <p className="section-subtitle">Stay compliant - Never miss a filing date</p>
          </div>
        </div>

        {/* Upcoming Deadlines Alert */}
        {upcomingDeadlines.length > 0 && (
          <div className="upcoming-deadlines-alert" data-testid="upcoming-deadlines">
            <div className="alert-header">
              <AlertTriangle size={20} />
              <span>Upcoming Deadlines This Month</span>
            </div>
            <div className="upcoming-list">
              {upcomingDeadlines.map((item, idx) => (
                <div key={idx} className={`upcoming-item ${item.urgent ? 'urgent' : ''}`}>
                  <Clock size={16} />
                  <span className="upcoming-date">{item.date}</span>
                  <span className="upcoming-desc">{item.desc}</span>
                  <span className={`upcoming-badge ${item.category.toLowerCase()}`}>{item.category}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Calendar Grid */}
        <div className="tax-calendar-grid">
          {taxDeadlines.map((category, idx) => (
            <div key={idx} className="tax-category-card" style={{ borderTopColor: category.color }}>
              <div className="tax-category-header" style={{ color: category.color }}>
                {category.icon}
                <h3>{category.category} Deadlines</h3>
              </div>
              <ul className="tax-deadline-list">
                {category.deadlines.map((deadline, dIdx) => (
                  <li key={dIdx} className="tax-deadline-item">
                    <span className="deadline-date">{deadline.date}</span>
                    <span className="deadline-desc">{deadline.description}</span>
                    <span className={`deadline-type ${deadline.type}`}>{deadline.type}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="tax-calendar-cta">
          <p>Need help with tax compliance? We ensure timely filing for all your returns.</p>
          <a href="/contact" className="btn btn-primary" data-testid="tax-calendar-cta">
            Get Compliance Support
          </a>
        </div>
      </div>
    </section>
  );
};

export default TaxCalendar;
